import React, { useState } from "react";
import { TextField, Grid, Typography, Autocomplete, Avatar, Box, IconButton, Button } from "@mui/material";
import { useFormikContext } from "formik";
import ReusableDialog from "../../../../common/dialog/dialog";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditIcon from "@mui/icons-material/Edit";

const legalStatuses = ["Status1", "Status2", "Status3"];

const YoungPersonInformation = () => {
  const [open, setOpen] = useState(false);
  const [avatarImage, setAvatarImage] = useState("/images/person.png");
  const [dateTaken, setDateTaken] = useState("");
  const { values, handleChange, handleBlur, setFieldValue, touched, errors } = useFormikContext();

  // Modified handleSave function to receive the form values from Formik
  const handleSave = (values) => {
    // Check if a file was uploaded and create the object URL
    if (values.file) {
      const fileUrl = URL.createObjectURL(values.file); // Create URL for the selected image
      setAvatarImage(fileUrl); // Set the selected avatar image
    }
    setDateTaken(values.dateTaken); // Set the date taken
    setOpen(false); // Close the dialog
  };
  const isSaveDisabled = !values.file || !values.dateTaken;

  return (
    <>
      <Typography sx={{ fontSize: "18px", color: "black" }} mt={5}>
        Young Person Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Name'
                name='name'
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Date of Birth
              </Typography>
              <TextField
                fullWidth
                name='dob'
                type='date'
                InputLabelProps={{ shrink: true }}
                value={values.dob}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dob && Boolean(errors.dob)}
                helperText={touched.dob && errors.dob}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Age
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Age'
                name='age'
                type='number'
                value={values.age}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.age && Boolean(errors.age)}
                helperText={touched.age && errors.age}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={5}>
              <Autocomplete
                id='legalStatus'
                options={legalStatuses}
                value={values.legalStatus}
                onChange={(event, newValue) => setFieldValue("legalStatus", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                      Legal Status
                    </Typography>
                    <TextField
                      {...params}
                      placeholder='Legal Status'
                      error={Boolean(touched.legalStatus && errors.legalStatus)}
                      helperText={touched.legalStatus && errors.legalStatus ? errors.legalStatus : ""}
                    />
                  </>
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                NI Number
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your NI Number'
                name='niNumber'
                value={values.niNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.niNumber && Boolean(errors.niNumber)}
                helperText={touched.niNumber && errors.niNumber}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={5} mt={5}>
          <Grid container>
            <Grid item>
              {/* Avatar Box */}
              <Avatar
                alt="Child's Picture"
                src={avatarImage} // The selected avatar image will be displayed here
                sx={{ width: 210, height: 250, marginRight: "20px", borderRadius: "0" }}
              />
              {dateTaken && (
                <Typography variant='caption' color='textSecondary' fontWeight='bold'>
                  {dateTaken} {/* Display the selected date */}
                </Typography>
              )}
            </Grid>
            <Grid item>
              <Box mt={25}>
                <IconButton aria-label='edit' onClick={() => setOpen(true)}>
                  <EditIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>

          {/* Dialog for Uploading Image and Adding Date */}
          <ReusableDialog
            isOpen={open}
            onClose={() => setOpen(false)}
            title='Add another Pen picture?'
            contentText='Add the relevant details to the fields to add another person to the involved parties.'
            onSubmit={() => handleSave(values)} // Properly pass the form values to handleSave
            submitText='Save'
            cancelText='Cancel'
            disable={isSaveDisabled}
          >
            <Box mt={2}>
              <input
                style={{ display: "none" }}
                id='file-upload'
                type='file'
                accept='.jpg, .jpeg, .png'
                onChange={(event) => {
                  setFieldValue("file", event.currentTarget.files[0]); // Set the file in Formik's state
                }}
              />
              <label htmlFor='file-upload'>
                <Button component='span' startIcon={<CloudUploadIcon />}>
                  Drag & drop files or browse files
                </Button>
              </label>

              {values.file && (
                <Box mt={2}>
                  <Typography variant='body2' color='textSecondary' gutterBottom>
                    Selected file
                  </Typography>
                  <Box mt={1}>
                    <Typography>{values.file.name}</Typography> {/* Display original file name */}
                  </Box>
                </Box>
              )}

              {/* Date Taken Field */}
              <Grid item xs={12} sm={6} mt={2}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Date Taken
                </Typography>
                <TextField
                  fullWidth
                  name='dateTaken'
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  value={values.dateTaken}
                  onChange={handleChange} // Handle date input
                  onBlur={handleBlur}
                  error={touched.dateTaken && Boolean(errors.dateTaken)}
                  helperText={touched.dateTaken && errors.dateTaken}
                />
              </Grid>
            </Box>
          </ReusableDialog>
        </Grid>
      </Grid>
      <Grid item xs={12} mt={1}>
        <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
          Parental Responsibility
        </Typography>
        <TextField
          fullWidth
          name='parentalResponsibility'
          value={values.parentalResponsibility}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.parentalResponsibility && Boolean(errors.parentalResponsibility)}
          helperText={touched.parentalResponsibility && errors.parentalResponsibility}
          multiline
          rows={2}
        />
      </Grid>
    </>
  );
};

export default YoungPersonInformation;
